import { useState } from 'react';
import { X, Sparkles, AlertCircle, Check, Download, Info } from 'lucide-react';

export default function FullRewriteModal({
  scriptText,
  analysis,
  frameworkAnalyses,
  approvedBeatRewrites,
  isLoading,
  error,
  rewriteResult,
  onGenerate,
  onAccept,
  onClose
}) {
  const [preservationLevel, setPreservationLevel] = useState(60); // Default to "High Preservation"

  const getPreservationDescription = (level) => {
    if (level >= 80) return { color: 'blue', text: 'Maximum Preservation', desc: 'Minimal changes, only critical fixes' };
    if (level >= 60) return { color: 'green', text: 'High Preservation', desc: 'Keep voice & style, improve structure' };
    if (level >= 40) return { color: 'yellow', text: 'Balanced', desc: 'Fix structure, maintain core story' };
    if (level >= 20) return { color: 'orange', text: 'Creative Enhancement', desc: 'Significant improvements, preserve premise' };
    return { color: 'purple', text: 'Maximum Creativity', desc: 'Fresh take on the concept' };
  };

  const currentDesc = getPreservationDescription(preservationLevel);

  const estimatedCost = (analysis?.totalPages || 100) * 0.007; // Rough estimate
  const estimatedTime = Math.ceil((analysis?.totalPages || 100) / 2); // seconds

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
          <div>
            <h2 className="text-xl font-bold text-slate-900 flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <span>Full Script Rewrite</span>
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Generate a complete rewrite with AI-optimized structure
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {!rewriteResult && !isLoading && (
            <div className="p-8">
              {/* Preservation Control */}
              <div className="mb-8">
                <label className="block font-semibold text-slate-900 mb-4">
                  Creative Control: How much of your original should be preserved?
                </label>

                {/* Slider */}
                <div className="relative pt-1">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="10"
                    value={preservationLevel}
                    onChange={(e) => setPreservationLevel(parseInt(e.target.value))}
                    className="w-full h-3 bg-gradient-to-r from-purple-200 via-yellow-200 to-blue-200 rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right,
                        rgb(192 132 252) 0%,
                        rgb(252 211 77) 50%,
                        rgb(147 197 253) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>Max Creativity</span>
                    <span>Balanced</span>
                    <span>Max Preservation</span>
                  </div>
                </div>

                {/* Current Selection */}
                <div
                  className="mt-4 p-4 rounded-lg border"
                  style={{
                    backgroundColor: currentDesc.color === 'blue' ? '#dbeafe' :
                                   currentDesc.color === 'green' ? '#dcfce7' :
                                   currentDesc.color === 'yellow' ? '#fef9c3' :
                                   currentDesc.color === 'orange' ? '#ffedd5' :
                                   '#f3e8ff',
                    borderColor: currentDesc.color === 'blue' ? '#93c5fd' :
                                currentDesc.color === 'green' ? '#86efac' :
                                currentDesc.color === 'yellow' ? '#fde047' :
                                currentDesc.color === 'orange' ? '#fdba74' :
                                '#d8b4fe'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="font-semibold"
                      style={{
                        color: currentDesc.color === 'blue' ? '#1e3a8a' :
                               currentDesc.color === 'green' ? '#14532d' :
                               currentDesc.color === 'yellow' ? '#713f12' :
                               currentDesc.color === 'orange' ? '#7c2d12' :
                               '#581c87'
                      }}
                    >
                      {preservationLevel}% - {currentDesc.text}
                    </span>
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{
                        backgroundColor: currentDesc.color === 'blue' ? '#bfdbfe' :
                                       currentDesc.color === 'green' ? '#bbf7d0' :
                                       currentDesc.color === 'yellow' ? '#fef08a' :
                                       currentDesc.color === 'orange' ? '#fed7aa' :
                                       '#e9d5ff',
                        color: currentDesc.color === 'blue' ? '#1e3a8a' :
                               currentDesc.color === 'green' ? '#14532d' :
                               currentDesc.color === 'yellow' ? '#713f12' :
                               currentDesc.color === 'orange' ? '#7c2d12' :
                               '#581c87'
                      }}
                    >
                      {preservationLevel}
                    </span>
                  </div>
                  <p
                    className="text-sm"
                    style={{
                      color: currentDesc.color === 'blue' ? '#1e40af' :
                             currentDesc.color === 'green' ? '#15803d' :
                             currentDesc.color === 'yellow' ? '#a16207' :
                             currentDesc.color === 'orange' ? '#c2410c' :
                             '#6b21a8'
                    }}
                  >
                    {currentDesc.desc}
                  </p>
                </div>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-slate-900">{approvedBeatRewrites.length}</div>
                  <div className="text-sm text-slate-600">Approved Beat Rewrites</div>
                  <div className="text-xs text-slate-500 mt-1">Will guide AI style</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-slate-900">~{estimatedTime}s</div>
                  <div className="text-sm text-slate-600">Estimated Time</div>
                  <div className="text-xs text-slate-500 mt-1">Processing duration</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-2xl font-bold text-slate-900">${estimatedCost.toFixed(2)}</div>
                  <div className="text-sm text-slate-600">Estimated Cost</div>
                  <div className="text-xs text-slate-500 mt-1">Claude API usage</div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Before you generate:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• The AI will rewrite your entire screenplay based on framework analysis</li>
                    <li>• You'll see a side-by-side comparison before accepting</li>
                    <li>• Your original script is never modified unless you explicitly accept</li>
                    <li>• {approvedBeatRewrites.length > 0 ? 'Approved beat rewrites will guide the AI\'s style' : 'Consider approving some beat rewrites first for better results'}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <Sparkles className="w-20 h-20 text-purple-600 animate-pulse mb-6" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">AI is rewriting your screenplay...</h3>
              <p className="text-slate-600 mb-4">This may take {estimatedTime}-{estimatedTime + 30} seconds</p>
              <div className="w-64 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-purple-600 rounded-full animate-pulse" style={{width: '60%'}}></div>
              </div>
              <p className="text-xs text-slate-500 mt-4">Processing {analysis?.totalPages || 0} pages...</p>
            </div>
          )}

          {error && (
            <div className="p-8">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-3">
                <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-2">Rewrite Failed</h3>
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {rewriteResult && !isLoading && !error && (
            <div className="p-8">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="flex items-start space-x-3">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-green-900 mb-2">Rewrite Complete!</h3>
                    <p className="text-green-700 text-sm">
                      Your screenplay has been rewritten using {rewriteResult.settings.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-slate-900 mb-3">Issues Addressed:</h4>
                <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                  {rewriteResult.issuesAddressed}
                </pre>
              </div>

              <div className="text-center text-sm text-slate-600">
                <p>The rewritten screenplay is ready. You can preview it in the editor or download it.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          {!rewriteResult && !isLoading && (
            <>
              <div className="text-sm text-slate-600">
                <p>Adjust the slider to control how much of your original to preserve</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => onGenerate(preservationLevel)}
                  disabled={isLoading}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Generate Rewrite</span>
                </button>
              </div>
            </>
          )}

          {rewriteResult && !isLoading && (
            <div className="w-full flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Close
              </button>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    // Download rewritten script
                    const blob = new Blob([rewriteResult.rewritten], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'rewritten-screenplay.fountain';
                    a.click();
                    // Clean up the URL object
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                  }}
                  className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => onAccept(rewriteResult)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Apply to Editor</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
